import React, { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Investment, Property } from '../../types';
import { validatePropertyOwnership } from '../../utils/validation';
import { PropertySelect } from '../ui/PropertySelect';
import { api } from '../../services/api';

interface InvestmentFormProps {
  investorId: string;
  investorName: string;
  existingInvestments: Investment[];
  properties: Property[];
  initialInvestment?: Investment | null;
  submitLabel?: string;
  isLoading?: boolean;
  onSubmit: (investment: Omit<Investment, 'id' | 'createdAt' | 'investorName' | 'propertyName'>) => void;
  onCancel: () => void;
}

function round2(n: number): number {
  return Math.round(n * 100) / 100;
}

function parsePctInput(raw: string): number | null {
  const cleaned = raw.replace(',', '.').replace(/[^0-9.]/g, '');
  if (!cleaned || cleaned === '.') return null;
  const n = Number(cleaned);
  return Number.isFinite(n) ? n : null;
}

export const InvestmentForm: React.FC<InvestmentFormProps> = ({
  investorId,
  investorName,
  existingInvestments,
  properties,
  initialInvestment = null,
  submitLabel,
  isLoading = false,
  onSubmit,
  onCancel,
}) => {
  const [formData, setFormData] = useState({
    propertyId: '',
    amount: 0,
    ownershipPct: 0,
    startDate: new Date().toISOString().split('T')[0],
    notes: '',
  });

  const [amountDisplay, setAmountDisplay] = useState('');
  const [ownershipPctInput, setOwnershipPctInput] = useState('');
  const [selectedUnitIds, setSelectedUnitIds] = useState<string[]>([]);

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [validationInfo, setValidationInfo] = useState<{
    currentTotal: number;
    available: number;
  } | null>(null);

  const { data: unitsData } = useQuery({
    queryKey: ['units', formData.propertyId],
    queryFn: () => api.getUnits(formData.propertyId),
    enabled: !!formData.propertyId,
    staleTime: 30_000,
  });
  const propertyUnits: { id: string; unitNumber: string }[] = (
    Array.isArray(unitsData) ? unitsData : (unitsData?.data ?? [])
  ).map((u: any) => ({ id: u.id, unitNumber: u.unitNumber }));

  const selectedProperty = properties.find((p) => p.id === formData.propertyId);
  const propertyTotalValue = Number(selectedProperty?.totalValue ?? 0);
  const hasPropertyValue =
    Number.isFinite(propertyTotalValue) && propertyTotalValue > 0;

  const formatAmountDisplay = (amount: number) => {
    if (amount > 0) {
      setAmountDisplay(amount.toLocaleString('es-MX'));
    } else {
      setAmountDisplay('');
    }
  };

  useEffect(() => {
    if (!initialInvestment) return;
    const start = initialInvestment.startDate
      ? new Date(initialInvestment.startDate).toISOString().split('T')[0]
      : new Date().toISOString().split('T')[0];
    const amount = Number(initialInvestment.amount ?? 0);
    const pct = Number(initialInvestment.ownershipPct ?? 0);
    setFormData({
      propertyId: initialInvestment.propertyId || '',
      amount,
      ownershipPct: pct,
      startDate: start,
      notes: initialInvestment.notes ? String(initialInvestment.notes) : '',
    });
    setOwnershipPctInput(Number.isFinite(pct) && pct > 0 ? String(pct) : '');
    formatAmountDisplay(amount);
    setSelectedUnitIds(initialInvestment.unitIds ?? []);
  }, [initialInvestment]);

  useEffect(() => {
    if (formData.propertyId) {
      validatePropertyOwnership(
        existingInvestments,
        formData.propertyId,
        formData.ownershipPct,
      );

      const currentTotal = existingInvestments
        .filter(
          (inv) =>
            inv.propertyId === formData.propertyId && inv.status === 'active',
        )
        .reduce((sum, inv) => sum + inv.ownershipPct, 0);

      setValidationInfo({
        currentTotal,
        available: 100 - currentTotal,
      });
    } else {
      setValidationInfo(null);
    }
  }, [formData.propertyId, formData.ownershipPct, existingInvestments]);

  const handlePropertyChange = (propertyId: string) => {
    setFormData((prev) => ({ ...prev, propertyId }));
    setSelectedUnitIds([]);
    if (errors.propertyId) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next.propertyId;
        return next;
      });
    }

    const property = properties.find((p) => p.id === propertyId);
    const tv = Number(property?.totalValue ?? 0);
    if (!Number.isFinite(tv) || tv <= 0) return;

    const pct = parsePctInput(ownershipPctInput);
    if (pct != null && pct > 0) {
      const amount = round2(tv * (pct / 100));
      setFormData((prev) => ({
        ...prev,
        propertyId,
        amount,
        ownershipPct: pct,
      }));
      formatAmountDisplay(amount);
    }
  };

  const handlePctInputChange = (raw: string) => {
    setOwnershipPctInput(raw);
    if (errors.ownershipPct) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next.ownershipPct;
        return next;
      });
    }

    const pct = parsePctInput(raw);
    if (pct == null) {
      setFormData((prev) => ({ ...prev, ownershipPct: 0, amount: 0 }));
      setAmountDisplay('');
      return;
    }

    if (!hasPropertyValue) {
      setFormData((prev) => ({ ...prev, ownershipPct: pct }));
      return;
    }

    const amount = round2(propertyTotalValue * (pct / 100));
    setFormData((prev) => ({
      ...prev,
      ownershipPct: pct,
      amount,
    }));
    formatAmountDisplay(amount);
    if (errors.amount) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next.amount;
        return next;
      });
    }
  };

  const handleAmountInputChange = (raw: string) => {
    const numericValue = raw.replace(/[^0-9]/g, '');
    const numValue = numericValue ? parseInt(numericValue, 10) : 0;
    setAmountDisplay(raw.replace(/[^0-9,]/g, '') || numericValue);

    if (errors.amount) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next.amount;
        return next;
      });
    }

    if (numValue <= 0) {
      setFormData((prev) => ({ ...prev, amount: 0, ownershipPct: 0 }));
      setOwnershipPctInput('');
      return;
    }

    if (!hasPropertyValue) {
      setFormData((prev) => ({ ...prev, amount: numValue }));
      return;
    }

    const pctRaw = (numValue / propertyTotalValue) * 100;
    const pct = parseFloat(Math.min(pctRaw, 100).toFixed(4));
    setFormData((prev) => ({
      ...prev,
      amount: numValue,
      ownershipPct: pct,
    }));
    setOwnershipPctInput(String(pct));
    if (errors.ownershipPct) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next.ownershipPct;
        return next;
      });
    }
  };

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.propertyId) {
      newErrors.propertyId = 'Debe seleccionar una propiedad';
    }
    if (formData.amount <= 0 || !Number.isFinite(formData.amount)) {
      newErrors.amount = 'El monto debe ser mayor a 0';
    }
    if (!Number.isFinite(formData.ownershipPct) || formData.ownershipPct <= 0) {
      newErrors.ownershipPct = 'El porcentaje debe ser mayor a 0';
    }
    if (formData.ownershipPct > 100) {
      newErrors.ownershipPct = 'El porcentaje no puede exceder 100%';
    }

    if (formData.propertyId) {
      const alreadyExists = existingInvestments.some(
        (inv) =>
          inv.propertyId === formData.propertyId &&
          inv.status === 'active' &&
          inv.id !== initialInvestment?.id,
      );
      if (alreadyExists) {
        newErrors.propertyId =
          'Este inversionista ya tiene una inversión activa en esa propiedad. Edita la existente en lugar de crear una nueva.';
      }

      const validation = validatePropertyOwnership(
        existingInvestments,
        formData.propertyId,
        formData.ownershipPct,
        initialInvestment?.id,
      );

      if (!validation.valid) {
        newErrors.ownershipPct =
          validation.error || 'La participación excede el límite';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validate()) {
      onSubmit({
        investorId,
        propertyId: formData.propertyId,
        amount: Number(formData.amount),
        ownershipPct: Number(formData.ownershipPct),
        startDate: formData.startDate,
        status: 'active',
        notes: formData.notes || undefined,
        unitIds: selectedUnitIds,
      });
    }
  };

  const handleChange = (field: string, value: string | number) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
    if (errors[field]) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[field];
        return next;
      });
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="p-4 bg-primary/10 rounded-lg">
        <p className="text-sm text-gray-700">
          <span className="font-medium">Inversionista:</span> {investorName}
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Propiedad <span className="text-red-500">*</span>
          </label>
          <PropertySelect
            properties={properties}
            value={formData.propertyId}
            onChange={handlePropertyChange}
            error={errors.propertyId}
            placeholder="Seleccione una propiedad"
            showPropertyCost={false}
          />
          {selectedProperty && !hasPropertyValue && (
            <p className="mt-1 text-xs text-amber-700">
              Esta propiedad no tiene costo total registrado; defínelo al editar la
              propiedad para calcular monto y %.
            </p>
          )}
        </div>

        <div className="md:col-span-1">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Fecha de Inicio <span className="text-red-500">*</span>
          </label>
          <input
            type="date"
            value={formData.startDate}
            onChange={(e) => handleChange('startDate', e.target.value)}
            className="w-full px-3 py-2 bg-white text-gray-900 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Porcentaje de Participación <span className="text-red-500">*</span>
          </label>
          <div className="relative">
            <input
              type="text"
              inputMode="decimal"
              value={ownershipPctInput}
              onChange={(e) => handlePctInputChange(e.target.value)}
              className={`w-full pr-8 pl-3 py-2 bg-white text-gray-900 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary ${
                errors.ownershipPct ? 'border-red-500' : 'border-gray-300'
              }`}
              placeholder="Ej: 25"
              autoComplete="off"
            />
            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 text-sm pointer-events-none">
              %
            </span>
          </div>
          {hasPropertyValue && (
            <p className="mt-1 text-xs text-gray-500">
              Al escribir el %, el monto se calcula con el costo total (
              ${propertyTotalValue.toLocaleString('es-MX')}).
            </p>
          )}
          {errors.ownershipPct && (
            <p className="mt-1 text-sm text-red-500">{errors.ownershipPct}</p>
          )}
          {validationInfo && (
            <div className="mt-2">
              <div className="flex justify-between text-xs mb-1">
                <span className="text-gray-600">
                  Participación actual: {validationInfo.currentTotal.toFixed(1)}%
                </span>
                <span className="text-gray-600">
                  Disponible: {validationInfo.available.toFixed(1)}%
                </span>
              </div>
              <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
                <div
                  className={`h-full ${
                    validationInfo.currentTotal + formData.ownershipPct > 100
                      ? 'bg-red-500'
                      : validationInfo.currentTotal + formData.ownershipPct ===
                          100
                        ? 'bg-green-500'
                        : 'bg-accent'
                  }`}
                  style={{
                    width: `${Math.min(validationInfo.currentTotal + formData.ownershipPct, 100)}%`,
                  }}
                />
              </div>
            </div>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Monto de Inversión <span className="text-red-500">*</span>
          </label>
          <div className="relative">
            <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 font-medium">
              $
            </span>
            <input
              type="text"
              inputMode="numeric"
              value={amountDisplay}
              onChange={(e) => handleAmountInputChange(e.target.value)}
              onBlur={() => formatAmountDisplay(formData.amount)}
              onFocus={() => {
                if (formData.amount > 0) {
                  setAmountDisplay(String(formData.amount));
                }
              }}
              className={`w-full pl-8 pr-3 py-2 bg-white text-gray-900 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary ${
                errors.amount ? 'border-red-500' : 'border-gray-300'
              }`}
              placeholder="Ej: 1,000,000"
              autoComplete="off"
            />
          </div>
          {hasPropertyValue && (
            <p className="mt-1 text-xs text-gray-500">
              Al escribir el monto, el % se calcula con el costo total.
            </p>
          )}
          {errors.amount && (
            <p className="mt-1 text-sm text-red-500">{errors.amount}</p>
          )}
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Notas (opcional)
        </label>
        <textarea
          value={formData.notes}
          onChange={(e) => handleChange('notes', e.target.value)}
          rows={3}
          className="w-full px-3 py-2 bg-white text-gray-900 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary placeholder-gray-400"
          placeholder="Notas adicionales sobre esta inversión..."
        />
      </div>

      {formData.propertyId && propertyUnits.length > 0 && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Unidades asignadas{' '}
            <span className="text-gray-400 font-normal">(opcional)</span>
          </label>
          <p className="text-xs text-gray-500 mb-2">
            Selecciona las unidades físicas que corresponden a esta inversión.
          </p>
          <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-2 max-h-48 overflow-y-auto p-2 border border-gray-200 rounded-lg bg-gray-50">
            {propertyUnits.map((unit) => {
              const checked = selectedUnitIds.includes(unit.id);
              return (
                <label
                  key={unit.id}
                  className={`flex items-center gap-1.5 px-2 py-1.5 rounded-md cursor-pointer text-xs font-medium border transition-colors select-none ${
                    checked
                      ? 'bg-primary/10 border-primary text-primary'
                      : 'bg-white border-gray-200 text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <input
                    type="checkbox"
                    className="sr-only"
                    checked={checked}
                    onChange={() =>
                      setSelectedUnitIds((prev) =>
                        checked ? prev.filter((id) => id !== unit.id) : [...prev, unit.id],
                      )
                    }
                  />
                  <span
                    className={`w-3 h-3 rounded-sm border flex-shrink-0 flex items-center justify-center ${
                      checked ? 'bg-primary border-primary' : 'border-gray-300 bg-white'
                    }`}
                  >
                    {checked && (
                      <svg viewBox="0 0 8 8" className="w-2 h-2 text-white fill-current">
                        <path d="M1.5 4L3 5.5 6.5 2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" fill="none" />
                      </svg>
                    )}
                  </span>
                  {unit.unitNumber}
                </label>
              );
            })}
          </div>
          {selectedUnitIds.length > 0 && (
            <p className="mt-1.5 text-xs text-primary font-medium">
              {selectedUnitIds.length} unidad{selectedUnitIds.length > 1 ? 'es' : ''} seleccionada{selectedUnitIds.length > 1 ? 's' : ''}
            </p>
          )}
        </div>
      )}

      {selectedProperty && formData.amount > 0 && formData.ownershipPct > 0 && (
        <div className="p-4 bg-primary/10 rounded-lg">
          <h4 className="text-sm font-semibold text-gray-900 mb-2">
            Resumen de la Inversión
          </h4>
          <div className="space-y-1 text-sm text-gray-700">
            <div className="flex justify-between">
              <span>Propiedad:</span>
              <span className="font-medium">{selectedProperty.name}</span>
            </div>
            <div className="flex justify-between">
              <span>Monto:</span>
              <span className="font-medium">
                ${formData.amount.toLocaleString('es-MX')}
              </span>
            </div>
            <div className="flex justify-between">
              <span>Participación:</span>
              <span className="font-medium">
                {formData.ownershipPct.toFixed(2)}%
              </span>
            </div>
          </div>
        </div>
      )}

      <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
        <button
          type="button"
          onClick={onCancel}
          disabled={isLoading}
          className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Cancelar
        </button>
        <button
          type="submit"
          disabled={isLoading}
          className="px-4 py-2 bg-accent text-white rounded-lg hover:bg-accent/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
        >
          {isLoading && (
            <svg
              className="animate-spin h-4 w-4 text-white"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              ></circle>
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              ></path>
            </svg>
          )}
          <span>{submitLabel ||
            (initialInvestment ? 'Guardar Cambios' : 'Agregar Inversión')}</span>
        </button>
      </div>
    </form>
  );
};
